import React, { useEffect, useRef } from "react";
import Lottie from "lottie-react";
import styled from "styled-components";
import {
  fonts,
  colors,
  media,
  breakpoints,
} from "../../components/common/mixins";
import { useCountdown } from "./hooks/useCountdown";
import timerAnimation from "./timer-animation.json";

const TOTAL_FRAMES = 240; // timer-animation.json: linear trim path from frame 0 to 240
const DISPLAY_DURATION_S = 10 * 60; // UI always counts down from 10 minutes

function hexToRgb01(hex: string): [number, number, number] {
  const value = hex.replace("#", "");
  return [
    parseInt(value.slice(0, 2), 16) / 255,
    parseInt(value.slice(2, 4), 16) / 255,
    parseInt(value.slice(4, 6), 16) / 255,
  ];
}

const DANGER_RGB = hexToRgb01(colors.danger);

// timer-animation.json bakes in a single warning color; swap every fill/stroke to danger for the urgent state.
function withDangerPalette(data: object) {
  const clone = JSON.parse(JSON.stringify(data));
  const walk = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node && typeof node === "object") {
      if (
        (node.ty === "fl" || node.ty === "st") &&
        Array.isArray(node.c?.k) &&
        node.c.k.length === 3
      ) {
        node.c.k = DANGER_RGB;
      }
      Object.values(node).forEach(walk);
    }
  };
  walk(clone.layers);
  return clone;
}

const dangerTimerAnimation = withDangerPalette(timerAnimation);

const CountdownWrapper = styled.div`
  ${media.below(breakpoints.mobile)} {
    text-align: right;
  }
`;

const MobileTimer = styled.div<{ $isUrgent: boolean }>`
  display: none;
  ${media.below(breakpoints.mobile)} {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px 2px 4px;
    border-radius: 8px;
    border: 1px solid
      ${(props) => (props.$isUrgent ? colors.danger : colors.warning)};
    background: ${(props) =>
      props.$isUrgent ? colors.dangerAlert : colors.warningAlert};
    margin-bottom: 16px;
  }
`;

const MobileTime = styled.span<{ $isUrgent: boolean }>`
  ${fonts.normalTextSmallBold()};
  color: ${(props) => (props.$isUrgent ? colors.danger : colors.warning)};
  font-variant-numeric: tabular-nums;
`;

// VisuallyHidden is used to provide a label for the countdown timer
// for screen readers, while keeping it hidden visually.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const DesktopCounter = styled.p<{ $isUrgent: boolean }>`
  margin: 0;
  padding-bottom: 16px;
  ${fonts.normalTextSmallBold()};
  color: ${(props) => (props.$isUrgent ? colors.danger : colors.warning)};
  ${media.below(breakpoints.mobile)} {
    display: none;
  }
`;

export function CountdownDisplay({
  expiresAt,
  label,
}: {
  expiresAt: string;
  label: string;
}) {
  const { timeRemaining, showTimer, minutes, seconds } =
    useCountdown(expiresAt);
  const lottieRef = useRef<any>(null);

  const remainingS = minutes * 60 + seconds;
  const elapsed = 1 - Math.min(remainingS / DISPLAY_DURATION_S, 1);
  const frame = Math.max(0, Math.min(TOTAL_FRAMES, elapsed * TOTAL_FRAMES));
  const isUrgent = remainingS < 30;

  useEffect(() => {
    if (!showTimer || !lottieRef.current) return;
    if (window.innerWidth >= breakpoints.mobile) return;
    lottieRef.current.goToAndStop(frame, true);
  }, [frame, showTimer]);

  if (!showTimer) return null;

  return (
    <CountdownWrapper>
      <MobileTimer $isUrgent={isUrgent}>
        <Lottie
          lottieRef={lottieRef}
          animationData={isUrgent ? dangerTimerAnimation : timerAnimation}
          autoplay={false}
          loop={false}
          aria-hidden="true"
          style={{ width: 20, height: 20 }}
        />
        <MobileTime $isUrgent={isUrgent} aria-live="polite" aria-atomic="true">
          <VisuallyHidden>{label} </VisuallyHidden>
          {timeRemaining}
        </MobileTime>
      </MobileTimer>
      <DesktopCounter
        $isUrgent={isUrgent}
        aria-live="polite"
        aria-atomic="true"
      >
        {label} {timeRemaining}
      </DesktopCounter>
    </CountdownWrapper>
  );
}
